import React, { useState } from 'react';
import { X, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { formatDateUK } from '../utils/dateUtils';

const MessagePopup = ({ messages, onClose, onMarkRead }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!messages || messages.length === 0) {
    return null;
  }

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'medium':
        return <AlertTriangle className="w-5 h-5 text-warm" />;
      case 'low':
        return <Info className="w-5 h-5 text-accent" />;
      default:
        return <Info className="w-5 h-5 text-accent" />;
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: 'bg-red-100 text-red-800 border-red-300',
      medium: 'bg-warm-tint/60 text-warm border-warm/40',
      low: 'bg-accent-tint text-accent-deep border-line'
    };
    return badges[priority] || badges.low;
  };

  const handleMarkAllRead = async () => {
    // Mark all messages as read
    for (const msg of messages) {
      await onMarkRead(msg.id);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="message-popup">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-xl font-bold">New Messages ({messages.length})</h2>
          </div>
          <button
            onClick={handleMarkAllRead}
            className="text-white hover:bg-emerald-700 rounded-full p-2 transition"
            data-testid="close-popup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages List */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={message.id}
                className="border border-line rounded-lg p-4 hover:border-emerald-300 transition"
                data-testid={`message-item-${index}`}
              >
                {/* Message Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getPriorityIcon(message.priority)}
                    <h3 className="font-semibold text-lg text-ink">{message.title}</h3>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-semibold border ${getPriorityBadge(message.priority)}`}>
                    {message.priority?.toUpperCase() || 'MEDIUM'}
                  </span>
                </div>

                {/* Message Type Badge */}
                <div className="mb-2">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    message.message_type === 'general' 
                      ? 'bg-accent-tint text-ink' 
                      : 'bg-accent-tint text-accent-deep'
                  }`}>
                    {message.message_type === 'general' ? 'General Broadcast' : 'Competition Message'}
                  </span>
                </div>

                {/* Message Content */}
                <p className="text-ink-muted mb-3 whitespace-pre-wrap">{message.content}</p>

                {/* Message Footer */}
                <div className="flex items-center justify-between text-sm text-ink-soft pt-2 border-t border-line">
                  <span>
                    {message.created_at 
                      ? formatDateUK(new Date(message.created_at).toISOString().split('T')[0])
                      : 'N/A'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-app px-6 py-4 flex justify-end gap-3 border-t">
          <button
            onClick={handleMarkAllRead}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium"
            data-testid="mark-all-read"
          >
            Mark All as Read & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessagePopup;
